import { useState, useEffect } from "react";
import { Play, X } from "lucide-react";

// Click-to-enlarge video: shows a thumbnail (hotlinked from YouTube's own CDN
// — zero storage cost) with a play button; pressing play opens a large
// centered modal with the actual embed, rather than playing small inline.
export const VideoLightbox = ({ videoId, title, testId, thumbnail, startSeconds, caption }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testId}
        className="group relative w-full h-full block"
      >
        <img
          src={thumbnail || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
          onError={thumbnail ? undefined : (e) => { e.currentTarget.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`; }}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {caption && <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent pointer-events-none" />}
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <span className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[var(--brand-primary)] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <Play size={24} className="text-black ml-1" fill="black" />
          </span>
        </div>
        {caption && (
          <div className="absolute bottom-4 left-4 md:bottom-5 md:left-5 text-left pointer-events-none">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--brand-primary)] mb-1">— Watch</p>
            <p className="font-display text-lg md:text-xl text-white leading-tight hero-text-shadow">{caption}</p>
          </div>
        )}
      </button>
      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10"
          onClick={() => setOpen(false)}
          data-testid={`${testId}-modal`}
          role="dialog"
          aria-modal="true"
          aria-label={title}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid={`${testId}-modal-close`}
            aria-label="Close video"
            className="absolute top-4 right-4 md:top-8 md:right-8 text-white/80 hover:text-white transition-colors"
          >
            <X size={32} />
          </button>
          <div className="relative w-full max-w-5xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1${startSeconds ? `&start=${startSeconds}` : ""}`}
              title={title}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </>
  );
};
